/**
 * Fusion d'un résultat de recherche dans le document — EF-POI-005, EF-POI-010,
 * EF-POI-003, EF-POI-014.
 *
 * Une nouvelle recherche est **additive**. Elle ne supprime jamais un POI,
 * automatique ou manuel. La suppression des POI devenus obsolètes relève de la
 * seule purge EF-POI-016.
 */

import { sortCategories, type CategoryKey } from '../core/categories';
import { newId } from '../core/ids';
import { osmKey, type Passage, type Poi, type PoiId, type ProgressionInterval } from '../core/model';
import type { Coordinate } from '../geo/geodesy';
import type { PassageComputation } from '../geo/passages';
import type { InterpretedObject } from './response';

/** Plafond du nombre total de POI automatiques du document — EF-POI-014. */
export const RESULT_CAP = 10_000;

/** Seuil d'avertissement non bloquant — EF-POI-014. */
export const RESULT_WARNING = 7000;

export interface MergeContext {
  /** Corridor de la recherche en cours : il devient la portée des POI touchés. */
  readonly corridorMeters: number;
  readonly computePassages: (coordinate: Coordinate, reachMeters: number) => readonly PassageComputation[];
  readonly generateId?: () => PoiId;
}

export interface MergeOutcome {
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly addedCount: number;
  readonly mergedCount: number;
  readonly rejectedCount: number;
  /** Clés OSM vues dans ce lot, écartées comprises. */
  readonly seenKeys: ReadonlySet<string>;
  /** Clés OSM écartées par les filtres, avec leur cause. */
  readonly rejectedCauses: ReadonlyMap<string, string>;
}

function toPassages(computations: readonly PassageComputation[]): Passage[] {
  return [...computations]
    .sort((a, b) => a.progressionMeters - b.progressionMeters)
    .map((computation, index) => ({
      index: index + 1,
      progressionMeters: computation.progressionMeters,
      distanceToRouteMeters: computation.distanceToRouteMeters,
      computed: null,
    }));
}

function sameCategories(a: readonly CategoryKey[], b: readonly CategoryKey[]): boolean {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

/**
 * Fusionne un lot d'objets interprétés. La correspondance se fait sur le couple
 * `(type OSM, identifiant OSM)`, en préférant les données du nouveau résultat et
 * en conservant l'identifiant interne, l'épinglage, l'exclusion et la note.
 */
export function mergeInterpretedObjects(
  current: ReadonlyMap<PoiId, Poi>,
  interpreted: readonly InterpretedObject[],
  context: MergeContext,
): MergeOutcome {
  const generateId = context.generateId ?? newId;
  const pois = new Map(current);

  const byOsmKey = new Map<string, PoiId>();
  for (const poi of pois.values()) {
    if (poi.osm !== null) byOsmKey.set(osmKey(poi.osm), poi.id);
  }

  const seenKeys = new Set<string>();
  const rejectedCauses = new Map<string, string>();
  let addedCount = 0;
  let mergedCount = 0;
  let rejectedCount = 0;

  for (const item of interpreted) {
    const ref = { type: item.object.type, id: item.object.id, matchedTags: item.matchedTags };
    const key = osmKey(ref);
    // La déduplication absorbe le recouvrement entre tronçons consécutifs.
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const existingId = byOsmKey.get(key);

    if (item.exclusion.rejected) {
      rejectedCount += 1;
      if (item.exclusion.cause !== null) rejectedCauses.set(key, item.exclusion.cause);
      if (existingId !== undefined) {
        const existing = pois.get(existingId);
        if (existing !== undefined) {
          pois.set(existingId, {
            ...existing,
            userState: {
              ...existing.userState,
              presenceState: 'rejectedByFilter',
              rejectionCause: item.exclusion.cause,
            },
          });
        }
      }
      continue;
    }

    const categories = sortCategories(item.categories);
    const passages = toPassages(context.computePassages(item.object.coordinate, context.corridorMeters));

    if (existingId === undefined) {
      const id = generateId();
      pois.set(id, {
        id,
        origin: 'osm',
        osm: ref,
        categories,
        name: item.name,
        openingHours: item.openingHours,
        phone: item.phone,
        email: item.email,
        website: item.website,
        accessNote: item.accessNote,
        coordinate: item.object.coordinate,
        passageReachMeters: context.corridorMeters,
        passages,
        manualInput: null,
        userState: {
          excluded: false,
          pinned: false,
          note: null,
          presenceState: 'present',
          rejectionCause: null,
          outOfSearchedPortion: false,
          categoriesChanged: false,
        },
      });
      byOsmKey.set(key, id);
      addedCount += 1;
      continue;
    }

    const existing = pois.get(existingId);
    if (existing === undefined) continue;
    const categoriesChanged = !sameCategories(existing.categories, categories);

    pois.set(existingId, {
      ...existing,
      osm: ref,
      categories,
      name: item.name,
      openingHours: item.openingHours,
      phone: item.phone,
      email: item.email,
      website: item.website,
      accessNote: item.accessNote,
      coordinate: item.object.coordinate,
      // La fusion met à jour la portée de passage au corridor de la recherche.
      passageReachMeters: context.corridorMeters,
      passages,
      userState: {
        ...existing.userState,
        presenceState: 'present',
        rejectionCause: null,
        categoriesChanged: existing.userState.categoriesChanged || categoriesChanged,
      },
    });
    mergedCount += 1;
  }

  return { pois, addedCount, mergedCount, rejectedCount, seenKeys, rejectedCauses };
}

export interface PresenceContext {
  readonly seenKeys: ReadonlySet<string>;
  readonly rejectedCauses: ReadonlyMap<string, string>;
  readonly coveredRanges: readonly ProgressionInterval[];
  /** Portion recherchée courante, pour le marqueur « hors portion recherchée ». */
  readonly searchedFromMeters: number;
  readonly searchedToMeters: number;
}

function isCovered(progressionMeters: number, ranges: readonly ProgressionInterval[]): boolean {
  return ranges.some(
    (range) =>
      progressionMeters >= range.fromProgressionMeters && progressionMeters <= range.toProgressionMeters,
  );
}

/**
 * Établit l'état de présence de chaque POI automatique après une recherche.
 *
 * Un POI dont la zone n'a pas été couverte n'est jamais présenté comme absent :
 * l'application ne dispose d'aucune information à son sujet.
 */
export function finalizePresence(
  pois: ReadonlyMap<PoiId, Poi>,
  context: PresenceContext,
): ReadonlyMap<PoiId, Poi> {
  const updated = new Map<PoiId, Poi>();

  for (const [id, poi] of pois) {
    const outOfSearchedPortion = !poi.passages.some(
      (passage) =>
        passage.progressionMeters >= context.searchedFromMeters &&
        passage.progressionMeters <= context.searchedToMeters,
    );

    if (poi.origin === 'manual' || poi.osm === null) {
      updated.set(id, {
        ...poi,
        userState: { ...poi.userState, presenceState: 'present', outOfSearchedPortion },
      });
      continue;
    }

    const key = osmKey(poi.osm);
    if (context.seenKeys.has(key)) {
      const cause = context.rejectedCauses.get(key);
      updated.set(id, {
        ...poi,
        userState: {
          ...poi.userState,
          presenceState: cause === undefined ? 'present' : 'rejectedByFilter',
          rejectionCause: cause ?? null,
          outOfSearchedPortion,
        },
      });
      continue;
    }

    // Seule une progression située dans un intervalle couvert autorise l'état
    // `absentFromResult` ; ailleurs, l'état est `rangeNotCovered`.
    const covered = poi.passages.some((passage) => isCovered(passage.progressionMeters, context.coveredRanges));
    updated.set(id, {
      ...poi,
      userState: {
        ...poi.userState,
        presenceState: covered ? 'absentFromResult' : 'rangeNotCovered',
        rejectionCause: null,
        outOfSearchedPortion,
      },
    });
  }

  return updated;
}

/**
 * Nombre de POI automatiques du document au sens du plafond EF-POI-014.
 * Les POI manuels et les POI exclus par la curation ne sont pas comptés.
 */
export function countAutomaticPois(pois: ReadonlyMap<PoiId, Poi>): number {
  let count = 0;
  for (const poi of pois.values()) {
    if (poi.origin !== 'osm') continue;
    if (poi.userState.excluded) continue;
    count += 1;
  }
  return count;
}
